import { useState } from 'react';
import { useAdminQuotes } from '@/hooks/useAdmin';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StatusFilter } from '@/components/admin/StatusFilter';

const QUOTE_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  processing: { bg: '#fef3c7', text: '#92400e' },
  ready: { bg: 'var(--color-success-50)', text: 'var(--color-success-600)' },
  ordered: { bg: 'var(--color-primary-50)', text: 'var(--color-primary-700)' },
  expired: { bg: 'var(--bg-surface-sunken)', text: 'var(--text-tertiary)' },
};

export const AdminQuotesPage = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading, error } = useAdminQuotes(statusFilter || undefined);

  return (
    <div>
      <h1
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-6)',
        }}
      >
        Quotes
      </h1>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <StatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
          options={QUOTE_STATUS_OPTIONS}
        />
      </div>

      {isLoading && (
        <div style={{ paddingTop: 'var(--space-12)', paddingBottom: 'var(--space-12)' }}>
          <LoadingSpinner size="lg" />
        </div>
      )}

      {error && (
        <div
          className="text-center"
          style={{
            padding: 'var(--space-8)',
            color: 'var(--color-error-500)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Failed to load quotes. Please try again.
        </div>
      )}

      {data && data.data.length === 0 && (
        <div
          className="text-center"
          style={{
            padding: 'var(--space-12)',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          No quotes found
        </div>
      )}

      {data && data.data.length > 0 && (
        <>
          {/* Mobile card list */}
          <div className="mobile-card-list">
            {data.data.map((quote) => {
              const colors = STATUS_COLORS[quote.status] ?? STATUS_COLORS.expired;
              return (
                <div
                  key={quote.quoteId}
                  style={{
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border-default)',
                    backgroundColor: 'var(--bg-surface)',
                    padding: 'var(--space-3) var(--space-4)',
                  }}
                >
                  <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-2)' }}>
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-primary)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {quote.quoteId.slice(-8).toUpperCase()}
                    </span>
                    <span
                      style={{
                        borderRadius: 'var(--radius-full)',
                        padding: 'var(--space-1) var(--space-3)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        backgroundColor: colors.bg,
                        color: colors.text,
                        textTransform: 'capitalize',
                      }}
                    >
                      {quote.status}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-secondary)',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {quote.fileName}
                  </p>
                  <div className="flex items-center" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {quote.volumeCm3 > 0 ? `${quote.volumeCm3.toFixed(1)} cm3` : '--'}
                    </span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {new Date(quote.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div
            className="desktop-table overflow-hidden"
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
            }}
          >
            <div className="table-scroll">
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '560px' }}>
              <thead style={{ backgroundColor: 'var(--bg-surface-sunken)' }}>
                <tr>
                  {['Quote ID', 'File', 'Volume', 'Status', 'Created'].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        textAlign: 'left',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        textTransform: 'uppercase',
                        letterSpacing: 'var(--tracking-wide)',
                        color: 'var(--text-tertiary)',
                        borderBottom: '1px solid var(--border-default)',
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.map((quote) => {
                  const colors = STATUS_COLORS[quote.status] ?? STATUS_COLORS.expired;
                  return (
                    <tr
                      key={quote.quoteId}
                      style={{
                        borderBottom: '1px solid var(--border-subtle)',
                        transition: 'background-color var(--duration-fast) var(--ease-out)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-surface-sunken)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td
                        style={{
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text-primary)',
                          fontFamily: 'monospace',
                        }}
                      >
                        {quote.quoteId.slice(-8).toUpperCase()}
                      </td>
                      <td
                        style={{
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-secondary)',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {quote.fileName}
                      </td>
                      <td
                        style={{
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {quote.volumeCm3 > 0 ? `${quote.volumeCm3.toFixed(1)} cm3` : '--'}
                      </td>
                      <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            borderRadius: 'var(--radius-full)',
                            padding: 'var(--space-1) var(--space-3)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-medium)',
                            backgroundColor: colors.bg,
                            color: colors.text,
                            textTransform: 'capitalize',
                          }}
                        >
                          {quote.status}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-tertiary)',
                        }}
                      >
                        {new Date(quote.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
